import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Instagram } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { companyInfo, faqs } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

export const Contact = () => {
  const { language, t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Construir mensagem para WhatsApp
    const message = `*${language === 'pt' ? 'Nova Solicitação de Orçamento' : 'New Quote Request'}*%0A%0A` +
      `*${t.contact.form.name}:* ${formData.name}%0A` +
      `*${t.contact.form.email}:* ${formData.email}%0A` +
      `*${t.contact.form.phone}:* ${formData.phone}%0A` +
      `*${t.contact.form.service}:* ${formData.service || (language === 'pt' ? 'Não especificado' : 'Not specified')}%0A%0A` +
      `*${t.contact.form.message}:*%0A${formData.message}`;
    
    // Abrir WhatsApp com mensagem pré-preenchida
    window.open(`https://wa.me/${companyInfo.whatsapp}?text=${message}`, '_blank');
    
    toast(t.contact.toastRedirect, {
      description: t.contact.toastDesc,
    });
    
    setFormData({ name: '', email: '', phone: '', service: '', message: '' });
  };

  const handleEmailSubmit = () => {
    const subject = encodeURIComponent(`${language === 'pt' ? 'Solicitação de Orçamento' : 'Quote Request'} - JC FLOORS & TILES`);
    const body = encodeURIComponent(
      `${t.contact.form.name}: ${formData.name}\n` +
      `${t.contact.form.email}: ${formData.email}\n` +
      `${t.contact.form.phone}: ${formData.phone}\n` +
      `${t.contact.form.service}: ${formData.service || (language === 'pt' ? 'Não especificado' : 'Not specified')}\n\n` +
      `${t.contact.form.message}:\n${formData.message}`
    );
    
    window.location.href = `mailto:${companyInfo.email}?subject=${subject}&body=${body}`;
  };

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${companyInfo.whatsapp}`, '_blank');
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">{t.contact.title}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-2">
              <CardContent className="p-6">
                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t.contact.phone}</h3>
                    <p className="text-gray-600">{companyInfo.phone}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t.contact.email}</h3>
                    <p className="text-gray-600 break-all">{companyInfo.email}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t.contact.location}</h3>
                    <p className="text-gray-600">{companyInfo.address}</p>
                    <p className="text-gray-600">{companyInfo.locality}</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 mb-6">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Instagram</h3>
                    <a 
                      href={companyInfo.instagramUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-amber-600 hover:text-amber-700 font-medium transition-colors"
                      data-testid="contact-instagram-link"
                    >
                      {companyInfo.instagram}
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t.contact.hours}</h3>
                    <p className="text-gray-600 text-sm">
                      {language === 'pt' ? 'Segunda a Sexta: 8h às 18h | Sábado: 8h às 13h' : 'Monday to Friday: 8am to 6pm | Saturday: 8am to 1pm'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={handleWhatsAppClick}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg transition-colors"
            >
              <Phone className="w-5 h-5 mr-2" />
              {t.contact.whatsappButton}
            </Button>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-2">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t.contact.formTitle}</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">{t.contact.form.name} *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      placeholder={language === 'pt' ? 'Seu nome' : 'Your name'}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email">{t.contact.form.email} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder={language === 'pt' ? 'seu@email.com' : 'your@email.com'}
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">{t.contact.form.phone} *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        placeholder="+351 999 999 999"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="service">{t.contact.form.service}</Label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                    >
                      <option value="">{t.contact.form.selectService}</option>
                      <option value={language === 'pt' ? 'Cerâmica' : 'Ceramic'}>{language === 'pt' ? 'Cerâmica' : 'Ceramic'}</option>
                      <option value={language === 'pt' ? 'Porcelanato' : 'Porcelain'}>{language === 'pt' ? 'Porcelanato' : 'Porcelain'}</option>
                      <option value={language === 'pt' ? 'Pedra Natural' : 'Natural Stone'}>{language === 'pt' ? 'Pedra Natural' : 'Natural Stone'}</option>
                      <option value={language === 'pt' ? 'Piso Vinílico' : 'Vinyl Flooring'}>{language === 'pt' ? 'Piso Vinílico' : 'Vinyl Flooring'}</option>
                      <option value={language === 'pt' ? 'Laminado' : 'Laminate'}>{language === 'pt' ? 'Laminado' : 'Laminate'}</option>
                      <option value={language === 'pt' ? 'Azulejos Exterior' : 'Outdoor Tiles'}>{language === 'pt' ? 'Azulejos Exterior' : 'Outdoor Tiles'}</option>
                      <option value={t.contact.form.other}>{t.contact.form.other}</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="message">{t.contact.form.message} *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="mt-1"
                      rows={5}
                      placeholder={t.contact.form.messagePlaceholder}
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg transition-colors"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    {t.contact.form.sendWhatsApp}
                  </Button>
                  
                  <Button
                    type="button"
                    onClick={handleEmailSubmit}
                    variant="outline"
                    className="w-full border-2 border-amber-600 text-amber-600 hover:bg-amber-50 py-6 text-lg transition-colors"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    {t.contact.form.sendEmail}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{t.contact.faqTitle}</h2>
          <Card className="border-2">
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq) => (
                  <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                    <AccordionTrigger className="text-left font-semibold text-gray-900">
                      {faq.question[language]}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      {faq.answer[language]}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};